// package imports
import { Box, IconButton, Drawer, Skeleton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';
import { useTheme } from '@mui/material/styles';
import { Theme as MuiTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { KitButton } from 'components/common/cms-kit';

// local imports
import { useFetchConversationHistory } from './hooks/conversationHistoryhooks';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'services/hooks/useSearchParams';
import { newConversation } from './commonUtils';

type SidebarProps = {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  reset: () => void;
};

type Conversation = {
  id: string;
  question: string;
};

const Sidebar: React.FC<SidebarProps> = ({
  drawerOpen,
  setDrawerOpen,
  reset,
}) => {
  const theme = useTheme() as MuiTheme;
  const router = useRouter();
  const searchId = useSearchParams('cid');
  const { data, status } = useFetchConversationHistory();
  const queryClient = useQueryClient();

  const getConversationHistory = () => {
    if (status !== 'success' && status !== 'error') {
      return Array.from({ length: 10 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          animation={false}
          sx={[{
            width: '100%',
            fontSize: '18px'
          }, index === 0 ? {
            paddingTop: ''
          } : {
            paddingTop: '.7rem'
          }]}
        />
      ));
    }

    if (status === 'error') {
      return (
        <div className="text-gray-900 truncate cursor-pointer pt-2">
          <div className="text-red-500" title="Error loading conversations">
            Error loading conversations
          </div>
        </div>
      );
    }

    if (status === 'success' && data) {
      return data.map((conversation: Conversation, index: number) => {
        return (
          <div
            className={`text-gray-900 truncate cursor-pointer ${index === 0 ? 'pt-0' : 'pt-2'} ${
              searchId === conversation.id ? 'underline' : ''
            }`}
            id={`conversation-${conversation.id}`}
            key={conversation.id}
            onClick={() => {
              const nextSearchParams = new URLSearchParams();
              const currentPath = router.asPath.split('?')[0];
              nextSearchParams.set('cid', conversation.id);
              router.push(`${currentPath}?${nextSearchParams}`);
              setDrawerOpen(false);
            }}
          >
            <Tooltip title={`${conversation.question}`}>
              <span>{conversation.question}</span>
            </Tooltip>
          </div>
        );
      });
    }
  };

  return (
    <>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <KitButton
          style={{
            width: '100%',
            backgroundColor: theme.palette.primary.light2,
          }}
          round
          size="sm"
          onClick={() => newConversation(queryClient, router, reset)}
          title="New Conversation"
        >
          <div className="flex flex-row justify-between w-[80%]">
            New Conversation <MapsUgcOutlinedIcon />
          </div>
        </KitButton>
        <div>{getConversationHistory()}</div>
      </Box>
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="left"
        ModalProps={{
          keepMounted: false,
        }}
      >
        <div
          id="historyWrapper"
          className="w-[250px] p-2 h-full"
          role="presentation"
        >
          <div className="flex justify-end">
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{ color: theme.palette.text.dark }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="font-semibold text-lg flex items-center mb-4">
            Past Conversations
          </div>
          {getConversationHistory()}
        </div>
      </Drawer>
    </>
  );
};
export default Sidebar;